import { css } from '@emotion/react';
import { Autocomplete, TextField } from '@mui/material';
import { useAtomValue } from 'jotai';
import { type HTMLAttributes, useMemo } from 'react';
import { portfolioCompaniesAtom } from '../../../services/state/PortCosStateJ';

const companySelectorCss = css`
  width: 250px;
`;

const popperSx = { '&.MuiAutocomplete-popper': { zIndex: 'var(--mui-zIndex-modal)' } };

interface CompanyOption {
  id: number | null;
  name: string;
}

const DEFAULT_OPTION: CompanyOption = { id: null, name: 'Default' };

interface CompanyPreferenceSelectorProps {
  selectedCompanyId: number | null;
  onCompanyChange: (companyId: number | null) => void;
}

export function CompanyPreferenceSelector({
  selectedCompanyId,
  onCompanyChange,
}: CompanyPreferenceSelectorProps) {
  const portfolioCompanies = useAtomValue(portfolioCompaniesAtom);

  const companyOptions = useMemo<CompanyOption[]>(() => {
    const sorted = [...portfolioCompanies].sort((a, b) => a.name.localeCompare(b.name));
    return [DEFAULT_OPTION, ...sorted.map((c) => ({ id: c.id, name: c.name }))];
  }, [portfolioCompanies]);

  const selectedOption = useMemo(
    () => companyOptions.find((o) => o.id === selectedCompanyId) ?? DEFAULT_OPTION,
    [companyOptions, selectedCompanyId]
  );

  return (
    <Autocomplete
      css={companySelectorCss}
      size='small'
      openOnFocus
      blurOnSelect
      disableClearable
      options={companyOptions}
      value={selectedOption}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_: unknown, option: CompanyOption | null) => onCompanyChange(option?.id ?? null)}
      renderOption={(props: HTMLAttributes<HTMLLIElement>, option: CompanyOption) => (
        <li {...props} key={option.id ?? 'default'}>
          {option.name}
        </li>
      )}
      slotProps={{ popper: { sx: popperSx } }}
      renderInput={(params) => <TextField {...params} />}
    />
  );
}
